import { CENTINELA_CUSTODY_THRESHOLDS } from "../../../features/centinela/types";
import type {
  CentinelaCustodyThreshold,
  CentinelaTripCustody,
  CentinelaTripCustodyInput,
} from "../../../features/centinela/types";
import type {
  CentinelaCustodyExpiry,
  CentinelaUnitCustody,
} from "../../../features/centinela/types";

type Unknown = Record<string, unknown>;

const CUSTODY_DEFAULTS = {
  verification_interval_min: 60,
  speed_limit_kmh: 95,
  max_stop_min: 10,
  gps_timeout_min: 5,
  routine_call_interval_min: 120,
} as const satisfies Record<CentinelaCustodyThreshold, number>;

/**
 * El mínimo que se puede elegir para el silencio del GPS.
 *
 * ── Por qué no se ofrece menos ─────────────────────────────────────────────
 * El barrido de desconexión de Centinela recorta su lista de candidatas en
 * `PISO_MIN` —cinco minutos— así que una unidad no llega siquiera a evaluarse
 * antes de eso. Ofrecer 3 promete una detección que el servicio no puede dar, y
 * quien lo elige se queda creyendo que tiene una vigilancia más fina de la que
 * hay.
 *
 * Y cuando se eligió, dolió: una unidad con dos equipos que reportan de forma
 * irregular quedó en 3 minutos y abrió 159 casos de sin señal en 18 días —unos
 * 15 al día— por respiros normales. Medido sobre esa unidad: con 3 minutos, 60
 * huecos en cuatro días; con 5, tres.
 *
 * La API lo rechaza además en `validacion.js`, que es el candado de verdad.
 * Esto es para no ofrecer lo que allá se va a negar.
 */
export const GPS_TIMEOUT_MIN_PISO = 5;

export const CUSTODY_THRESHOLD_OPTIONS = {
  verification_interval_min: [15, 30, 45, 60, 90, 120, 180],
  speed_limit_kmh: [60, 80, 90, 95, 100, 110, 120, 140],
  max_stop_min: [5, 10, 15, 20, 30, 45, 60],
  gps_timeout_min: [GPS_TIMEOUT_MIN_PISO, 10, 15, 20, 30],
  routine_call_interval_min: [30, 60, 90, 120, 180, 240, 360],
} as const satisfies Record<CentinelaCustodyThreshold, readonly number[]>;

const asRecord = (value: unknown): Unknown =>
  value && typeof value === "object" ? (value as Unknown) : {};

const asText = (value: unknown) => (typeof value === "string" ? value : "");

const asStringList = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const asInt = (value: unknown, fallback: number) => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
};

export function mapCentinelaTripCustody(payload: unknown): CentinelaTripCustody {
  const root = asRecord(payload);
  const nested = root.custody;
  const custody = nested && typeof nested === "object" ? (nested as Unknown) : root;

  const thresholds = CENTINELA_CUSTODY_THRESHOLDS.reduce(
    (acc, key) => ({ ...acc, [key]: asInt(custody[key], CUSTODY_DEFAULTS[key]) }),
    {} as Record<CentinelaCustodyThreshold, number>,
  );

  return {
    ...thresholds,
    profile: asText(custody.profile),
    notify_user_ids: asStringList(custody.notify_user_ids),
  };
}

export function withServedOption(options: readonly number[], value: number) {
  return options.includes(value) ? [...options] : [...options, value].sort((a, b) => a - b);
}

export function hasThresholdChanges(
  served: CentinelaTripCustody,
  draft: CentinelaTripCustody,
) {
  return CENTINELA_CUSTODY_THRESHOLDS.some((key) => served[key] !== draft[key]);
}

export function hasCustodyChanges(
  served: CentinelaTripCustody,
  draft: CentinelaTripCustody,
) {
  const thresholdChanged = hasThresholdChanges(served, draft);

  const recipientsChanged =
    served.notify_user_ids.length !== draft.notify_user_ids.length ||
    served.notify_user_ids.some((userId) => !draft.notify_user_ids.includes(userId));

  return thresholdChanged || recipientsChanged;
}

export const CUSTOM_CUSTODY_PROFILE = "personalizado";

export type CustodyThresholds = Record<CentinelaCustodyThreshold, number>;

export interface CustodyProfileOption {
  id: string;
  name: string;
  description: string;
  thresholds: CustodyThresholds | null;
  alerts: string[] | null;
  callsPer4h: number | null;
  unlocksThresholds: boolean;
}

export interface CustodyParameterOption {
  field: CentinelaCustodyThreshold;
  label: string;
  unit: string;
  description: string;
}

const UNIT_LABEL_KEYS: Record<string, string> = {
  min: "monitoreoIa.units.custodyModal.minutes",
  "km/h": "monitoreoIa.units.custodyModal.kmh",
};

export const custodyUnitLabelKey = (unit: string): string | null =>
  UNIT_LABEL_KEYS[unit] ?? null;

const isThresholdField = (value: string): value is CentinelaCustodyThreshold =>
  (CENTINELA_CUSTODY_THRESHOLDS as readonly string[]).includes(value);

function readProfileThresholds(row: Unknown): CustodyThresholds | null {
  if (CENTINELA_CUSTODY_THRESHOLDS.some((key) => typeof row[key] !== "number")) return null;

  return CENTINELA_CUSTODY_THRESHOLDS.reduce(
    (acc, key) => ({ ...acc, [key]: asInt(row[key], CUSTODY_DEFAULTS[key]) }),
    {} as CustodyThresholds,
  );
}

export function mapCustodyProfiles(payload: unknown): CustodyProfileOption[] {
  const rows = asRecord(asRecord(payload).custody_profiles).perfiles;
  if (!Array.isArray(rows)) return [];

  return rows.flatMap((entry) => {
    const row = asRecord(entry);
    const id = asText(row.id);
    if (!id) return [];

    return [
      {
        id,
        name: asText(row.nombre) || id,
        description: asText(row.descripcion),
        thresholds: readProfileThresholds(row),
        alerts: Array.isArray(row.alertas) ? asStringList(row.alertas) : null,
        callsPer4h:
          typeof row.llamadas_estimadas_por_4h === "number"
            ? row.llamadas_estimadas_por_4h
            : null,
        unlocksThresholds: row.libera_selectores === true,
      },
    ];
  });
}

export function mapCustodyParameters(payload: unknown): CustodyParameterOption[] {
  const rows = asRecord(asRecord(payload).custody_profiles).parametros;
  if (!Array.isArray(rows)) return [];

  return rows.flatMap((entry) => {
    const row = asRecord(entry);
    const field = asText(row.campo);
    if (!isThresholdField(field)) return [];

    return [
      {
        field,
        label: asText(row.etiqueta) || field,
        unit: asText(row.unidad),
        description: asText(row.descripcion),
      },
    ];
  });
}

export const custodyPresets = (profiles: CustodyProfileOption[]) =>
  profiles.filter((profile) => !profile.unlocksThresholds);

export function isPresetProfile(profiles: CustodyProfileOption[], profileId: string) {
  const found = profiles.find((profile) => profile.id === profileId);
  return Boolean(found && !found.unlocksThresholds);
}

export function matchCustodyProfile(
  profiles: CustodyProfileOption[],
  thresholds: CustodyThresholds,
): string | null {
  const matched = custodyPresets(profiles).find(
    (profile) =>
      profile.thresholds !== null &&
      CENTINELA_CUSTODY_THRESHOLDS.every(
        (key) => profile.thresholds?.[key] === thresholds[key],
      ),
  );

  return matched?.id ?? null;
}

export function toTripCustodyInput(
  served: CentinelaTripCustody,
  draft: CentinelaTripCustody,
  profiles: CustodyProfileOption[],
  pickedProfile?: string,
): CentinelaTripCustodyInput {
  const profile =
    pickedProfile ??
    (hasThresholdChanges(served, draft)
      ? CUSTOM_CUSTODY_PROFILE
      : served.profile.trim() || undefined);

  const recipients = { notify_user_ids: draft.notify_user_ids };

  if (profile && isPresetProfile(profiles, profile)) return { ...recipients, profile };

  const thresholds = CENTINELA_CUSTODY_THRESHOLDS.reduce(
    (acc, key) => ({ ...acc, [key]: draft[key] }),
    {} as CustodyThresholds,
  );

  return profile
    ? { ...recipients, ...thresholds, profile }
    : { ...recipients, ...thresholds };
}

const UNIT_CUSTODY_DEFAULTS = {
  speed_limit_kmh: 80,
  gps_timeout_min: 15,
} as const;

const HOUR_MS = 60 * 60 * 1000;

const MAX_EXPIRY_HOURS = 720;

const asBoolean = (value: unknown, fallback: boolean) =>
  typeof value === "boolean" ? value : fallback;

const asNullableInt = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

function readExpiry(value: unknown): CentinelaCustodyExpiry | null {
  const row = asRecord(value);
  const unidad = asText(row.unidad);

  if (unidad === "permanente") return { unidad: "permanente" };
  if (unidad !== "horas" && unidad !== "dias" && unidad !== "meses" && unidad !== "anios") {
    return null;
  }

  const cantidad = asNullableInt(row.cantidad);
  return cantidad && cantidad > 0 ? { unidad, cantidad } : null;
}

export function mapCentinelaUnitCustody(payload: unknown): CentinelaUnitCustody | null {
  const root = asRecord(payload);
  if (!("custody" in root)) return null;
  if (root.custody === null) return null;

  const custody = asRecord(root.custody);

  return {
    device_id: asText(custody.device_id),
    activa: asBoolean(custody.activa, false),
    vigente: asBoolean(custody.vigente, false),
    permanente: asBoolean(custody.permanente, false),
    expira_en: asText(custody.expira_en) || null,
    expira_en_min: asNullableInt(custody.expira_en_min),
    expiracion: readExpiry(custody.expiracion),
    alertas_no_aplicables: asStringList(custody.alertas_no_aplicables),
    profile: asText(custody.profile),
    speed_limit_kmh: asInt(custody.speed_limit_kmh, UNIT_CUSTODY_DEFAULTS.speed_limit_kmh),
    gps_timeout_min: asInt(custody.gps_timeout_min, UNIT_CUSTODY_DEFAULTS.gps_timeout_min),
    notify_user_ids: asStringList(custody.notify_user_ids),
  };
}

export const PERMANENT_CUSTODY_EXPIRY: CentinelaCustodyExpiry = { unidad: "permanente" };

export function toCustodyExpiry(expiresAt: Date): CentinelaCustodyExpiry {
  const hours = Math.max(1, Math.ceil((expiresAt.getTime() - Date.now()) / HOUR_MS));

  return hours <= MAX_EXPIRY_HOURS
    ? { unidad: "horas", cantidad: hours }
    : { unidad: "dias", cantidad: Math.ceil(hours / 24) };
}
